import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ConfirmationComponent } from '../../../../notification-preferences/components/confirmation/confirmation.component';
import { selectPatientId } from '../../../../user/store/selectors/user.selectors';
import { ScheduleAppointmentHelperService } from '../../../services/schedule-appointment-helper/schedule-appointment-helper.service';
import { AppointmentSelectionComponent } from '../appointment-selection/appointment-selection.component';
import { ProviderLocationSelectionComponent } from '../provider-location-selection/provider-location-selection.component';
import { ProviderSelectionComponent } from '../provider-selection/provider-selection.component';
import { VisitTypeSelectionComponent } from '../visit-type-selection/visit-type-selection.component';

export type ScheduleAppointmentStepData = {
  title: string;
  showBackButton: boolean;
  primaryButtonText: string | null;
  action: (() => void) | null;
};

@Component({
  selector: 'app-schedule-appointment-dialog',
  imports: [
    MatDialogModule,
    CommonModule,
    MatButtonModule,
    ProviderSelectionComponent,
    VisitTypeSelectionComponent,
    ProviderLocationSelectionComponent,
    AppointmentSelectionComponent,
    ConfirmationComponent,
  ],
  templateUrl: './schedule-appointment-dialog.component.html',
  styleUrl: './schedule-appointment-dialog.component.scss',
})
export class ScheduleAppointmentDialogComponent implements OnDestroy {
  private store = inject(Store);
  private scheduleAppointmentHelperService = inject(ScheduleAppointmentHelperService);

  @ViewChild(AppointmentSelectionComponent) appointmentSelectionComponent!: AppointmentSelectionComponent;
  @ViewChild(ConfirmationComponent) confirmAppointmentComponent!: ConfirmationComponent;

  stepDataMap = new Map<number, ScheduleAppointmentStepData>([
    [1, { title: 'Provider', showBackButton: false, primaryButtonText: null, action: null }],
    [
      2,
      {
        title: 'Reason for Visit',
        showBackButton: true,
        primaryButtonText: null,
        action: null,
      },
    ],
    [
      3,
      {
        title: 'Provider Location',
        showBackButton: true,
        primaryButtonText: null,
        action: null,
      },
    ],
    [
      4,
      {
        title: 'Appointment Day/Time',
        showBackButton: true,
        primaryButtonText: 'Request',
        action: () => this.requestAppointment(),
      },
    ],
    [
      5,
      {
        title: 'Confirmation',
        showBackButton: false,
        primaryButtonText: 'Save',
        action: () => this.savePreferences(),
      },
    ],
  ]);

  isVirtualVisitType = false;
  providerUserId: string | null = null;
  visitTypeId: number | null = null;
  teamId: number | null = null;

  patientId$ = this.store.select(selectPatientId);

  get currentStep(): 1 | 2 | 3 | 4 | 5 {
    return this.scheduleAppointmentHelperService.currentStep;
  }

  get showBackButton(): boolean {
    return this.stepDataMap.get(this.currentStep)?.showBackButton ?? false;
  }

  get primaryButtonText(): string | null {
    return this.stepDataMap.get(this.currentStep)?.primaryButtonText ?? null;
  }

  get action(): (() => void) | null {
    return this.stepDataMap.get(this.currentStep)?.action ?? null;
  }

  get title(): string {
    return this.stepDataMap.get(this.currentStep)?.title ?? '';
  }

  onSelection(value: string | number, isVirtualVisitType?: boolean): void {
    switch (this.currentStep) {
      case 1:
        this.providerUserId = value as string;
        break;
      case 2:
        this.visitTypeId = value as number;
        this.isVirtualVisitType = isVirtualVisitType ?? false;
        break;
      case 3:
        this.teamId = value as number;
        break;
    }
    this.scheduleAppointmentHelperService.nextStep(this.isVirtualVisitType);
  }

  goBack(): void {
    switch (this.currentStep) {
      case 2:
        this.providerUserId = null;
        break;
      case 3:
        this.visitTypeId = null;
        break;
      case 4:
        this.teamId = null;
        break;
    }
    this.scheduleAppointmentHelperService.previousStep(this.isVirtualVisitType);
  }

  requestAppointment(): void {
    this.appointmentSelectionComponent.requestAppointment();
  }

  savePreferences(): void {
    this.confirmAppointmentComponent.confirmationType = 'appointment';
    this.confirmAppointmentComponent.triggerUpdatePreferences();
  }

  cancel(): void {
    if (this.currentStep === 4) {
      this.appointmentSelectionComponent.onCancel();
    } else {
      this.scheduleAppointmentHelperService.closeDialog();
    }
  }

  ngOnDestroy(): void {
    this.scheduleAppointmentHelperService.resetSteps();
  }
}
